<script lang="ts">
    import { createEventDispatcher, onMount } from 'svelte'
    import type { Purchase } from '../../modules/paymentsApi/purchases/purchase.entity'
    import type { Gateway } from '../../modules/paymentsApi/gateways/gateway.entity'
    import Zinput from '../_common/z/zinput.svelte'
    import Zact from '../_common/z/zact.svelte'
    import Zframe from '../_common/z/zframe.svelte'
    import Zbutton from '../_common/z/zbutton.svelte'
    import ZContainer from '../_common/z/z-container.svelte'
    import dayjs from 'dayjs'
    import { PaymentMethods, PaymentMethodsFriendly } from '../../modules/paymentsApi/charges/charge.enum'
    import { PurchaseTypeFriendly } from '../../modules/paymentsApi/purchases/purchase.enum'
    import { ChangeGatewayInputDto, ChangeGatewayOutputDto } from '../../modules/paymentsApi/purchases/dto/change-gateway.dto'

    // ##############################################################################################################
    const dispatch = createEventDispatcher()

    export let purchaseId: string

    let entity: ChangeGatewayInputDto = new ChangeGatewayInputDto()
    let purchase: Purchase = null
    let gateways: Gateway[] = []

    let step = 1
    let saving = false
    let show = true
    let simulation: ChangeGatewayOutputDto = null

    async function load() {
        try {
            show = true
            purchase = await window.api.payments.purchases.getOne(purchaseId, { invoices: true })
            gateways = await window.api.payments.gateways.getAll()
        } catch (error) {
            window.error(error.message || error)
        } finally {
            show = false
        }
    }

    onMount(async () => await load())

    // Cartão não troca: o token pertence ao gateway que o emitiu e não atravessa para outro. Para
    // migrar seria preciso o titular informar o cartão de novo no gateway novo.
    $: isCard = purchase?.purchaseMethod === PaymentMethods.credit

    // Só entram na lista os gateways que aceitam o meio E o tipo de cobrança desta assinatura. As
    // capacidades vêm calculadas do backend, a partir do adapter de cada gateway — não são uma
    // lista mantida aqui, que divergiria da implementação.
    function gatewayOptions(): { value: string; label: string }[] {
        if (!purchase) return []
        return gateways
            .filter((gateway) => gateway.gatewayId !== purchase.gatewayId)
            .filter((gateway) => gateway.status === 100)
            .filter((gateway) => {
                const meios = gateway.gatewayPaymentMethods || []
                if (meios.length && !meios.includes(purchase.purchaseMethod)) return false
                const tipos = gateway.gatewayPurchaseTypes?.[purchase.purchaseMethod]
                if (tipos && !tipos.includes(purchase.purchaseType)) return false
                return true
            })
            .map((gateway) => ({ value: gateway.gatewayId, label: gateway.label }))
    }

    // a lista entra como parâmetro para que a expressão do template dependa dela: sem isso o texto
    // não se atualiza quando os gateways chegam
    function gatewayLabel(gatewayId: string, lista: Gateway[]): string {
        return lista?.find((el) => el.gatewayId === gatewayId)?.label || gatewayId
    }

    async function goSimulate() {
        try {
            if (!entity.gatewayId) {
                window.error('Selecione o gateway de destino')
                return
            }
            entity.simulation = true
            simulation = await window.api.payments.purchases.changeGateway(purchaseId, entity)
            step = 2
        } catch (error) {
            window.error(error.message || error)
        }
    }

    async function goSave() {
        try {
            saving = true
            entity.simulation = false
            await window.api.payments.purchases.changeGateway(purchaseId, entity)
            dispatch('close')
        } catch (error) {
            window.error(error.message || error)
        } finally {
            saving = false
        }
    }
</script>

<div class="root">
    <Zact>
        <div class="spacer" />
        {#if window.store.token.permissions.pay.entries.write && !isCard}
            {#if step === 1}
                <Zbutton label="Simular" on:click={goSimulate}></Zbutton>
            {:else}
                <Zbutton label="Voltar" color="yellow" on:click={() => (step = 1)}></Zbutton>
                <Zbutton label="Confirmar troca" color="green" loading={saving} disabled={saving} on:click={goSave}></Zbutton>
            {/if}
        {/if}
    </Zact>

    {#if !show && purchase}
        {#if isCard}
            <div class="alert alert-warning text-center m-3">
                Assinaturas no cartão não podem trocar de gateway. O cartão fica registrado no gateway que o emitiu, então a migração exige que o titular
                informe os dados novamente em uma nova cobrança.
            </div>
        {:else if gatewayOptions().length === 0}
            <div class="alert alert-warning text-center m-3">
                Nenhum outro gateway ativo aceita cobrança por <strong>{PaymentMethodsFriendly[purchase.purchaseMethod] || purchase.purchaseMethod}</strong>
                no formato <strong>{PurchaseTypeFriendly[purchase.purchaseType] || purchase.purchaseType}</strong>.
            </div>
        {:else}
            <div class="alert alert-primary m-3">
                As parcelas ainda não cobradas passam a ser cobradas no gateway novo. As já cobradas continuam no atual, para que a consulta e a baixa
                sigam encontrando o pagamento onde ele foi feito.
            </div>

            <Zframe title="Trocar gateway" class="m-3">
                {#if step === 1}
                    <ZContainer gapRow="8px" gapCol="8px">
                        <Zinput size={6} sizeM={12} label="Gateway atual" type="text" value={gatewayLabel(purchase.gatewayId, gateways)} disabled={true}></Zinput>
                        <Zinput size={6} sizeM={12} label="Novo gateway" type="select" options={gatewayOptions()} bind:value={entity.gatewayId}></Zinput>
                    </ZContainer>
                {:else}
                    <div>
                        De <strong>{gatewayLabel(simulation.fromGatewayId, gateways)}</strong> para <strong>{gatewayLabel(simulation.toGatewayId, gateways)}</strong>.
                    </div>

                    <div class="mt-2">
                        {#if simulation.chargesToMove.length === 0}
                            Nenhuma parcela em aberto será movida agora. As próximas parcelas geradas já nascem no gateway novo.
                        {:else}
                            {simulation.chargesToMove.length} parcela(s) passam a ser cobradas no gateway novo:
                            <ul>
                                {#each simulation.chargesToMove as charge}
                                    <li>
                                        {dayjs(charge.dueDate).format('DD/MM/YYYY')} — {window.api.utils.utils.toReal(charge.value)}
                                    </li>
                                {/each}
                            </ul>
                        {/if}
                    </div>

                    {#if (simulation.retryingAtPreviousGateway || []).length > 0}
                        <!-- já têm pedido no gateway onde foram tentadas: a nova tentativa acontece lá, não no novo -->
                        <div class="alert alert-warning mt-2">
                            {simulation.retryingAtPreviousGateway.length} parcela(s) que já tiveram tentativa de cobrança seguem sendo cobradas no gateway em que
                            foram tentadas:
                            <ul class="mb-0">
                                {#each simulation.retryingAtPreviousGateway as charge}
                                    <li>
                                        {dayjs(charge.dueDate).format('DD/MM/YYYY')} — {gatewayLabel(charge.gatewayId, gateways)}
                                    </li>
                                {/each}
                            </ul>
                        </div>
                    {/if}

                    {#if simulation.openAtPreviousGateway.length > 0}
                        <!-- um boleto emitido ou um QR pix seguem pagáveis no gateway anterior até vencerem -->
                        <div class="alert alert-warning mt-2">
                            Atenção: {simulation.openAtPreviousGateway.length} cobrança(s) já emitida(s) continuam pagáveis no gateway atual até vencerem.
                            <ul class="mb-0">
                                {#each simulation.openAtPreviousGateway as charge}
                                    <li>
                                        {dayjs(charge.dueDate).format('DD/MM/YYYY')} — {PaymentMethodsFriendly[charge.paymentMethod] ||
                                            charge.paymentMethod}
                                    </li>
                                {/each}
                            </ul>
                        </div>
                    {/if}
                {/if}
            </Zframe>
        {/if}
    {/if}
</div>
